<template>
  <v-dialog max-width="320" :value="show" @input="closeModal">
    <v-card class="monthly-activities-modal">
      <v-card-title class="modal-header">
        <div>
          <h3 class="modal-title">{{ 'activities.over_the_last_30_days' | translate }}</h3>
          <p v-if="client && farm" class="client-name">{{ farm.name || '' }}</p>
        </div>
      </v-card-title>

      <v-card-content class="modal-content">
        <!-- Loading State -->
        <div v-if="loading" class="loading-container">
          <p class="loading-text">{{ 'activities.loading' | translate }}...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <p class="error-text">{{ error }}</p>
          <v-btn @click="loadData" color="primary" text small>
            {{ 'activities.try_again' | translate }}
          </v-btn>
        </div>

        <!-- Data Table -->
        <div v-else class="activities-table">
          <!-- Table Header -->
          <div class="table-header-row">
            <div class="header-cell status-header">{{ 'activities.status' | translate }}</div>
            <div class="header-cell days-header">{{ 'activities.number_of_days' | translate }}</div>
            <div class="header-cell loads-header">{{ 'activities.number_of_downloads' | translate }}</div>
          </div>

          <!-- Status Rows -->
          <div v-for="activity in transformedActivities" :key="activity.status"
            :class="['activity-row', activity.className]">
            <div class="status-label">{{ activity.status }}</div>
            <div class="status-days">{{ activity.days }}</div>
            <div class="status-loads">{{ activity.loadCount }}</div>
          </div>

          <!-- Empty State -->
          <div v-if="transformedActivities.length === 0" class="empty-state">
            <p>{{ 'activities.no_data' | translate }}</p>
          </div>
        </div>
      </v-card-content>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api'

export default {
  name: 'MonthlyFarmActivitiesModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    client: {
      type: [Object],
      required: false,
      default: null
    },
    farm: {
      type: [Object],
      required: false,
      default: null
    }
  },

  data () {
    return {
      loading: false,
      error: null,
      statisticsData: null
    }
  },

  computed: {
    transformedActivities () {
      if (!this.statisticsData) return []

      return this.mapApiDataToActivities(this.statisticsData)
    }
  },

  watch: {

    '$props': {
      handler () {
        this.checkAndLoadData()
      },
      deep: true,
      immediate: true
    }
  },

  mounted () {
    document.addEventListener('keydown', this.handleEscapeKey)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.handleEscapeKey)
  },

  methods: {
    checkAndLoadData () {
      if (this.show && this.client.id && !this.loading) {
        this.loadData()
      }
    },

    async loadData () {
      if (!this.client.id) {
        this.error = 'Не вказано ID клієнта'
        return
      }

      if (this.loading) {
        return
      }

      this.loading = true
      this.error = null

      try {
        const params = {
          farm: this.farm.id
        }

        const res = await api.dashboard.groups.getClientFarmStats(this.client.id, params)
        this.statisticsData = res.data || res
      } catch (error) {
        console.error('Failed to load client schedule statistics:', error)
        this.error = 'Помилка завантаження статистики. Спробуйте пізніше.'
      } finally {
        this.loading = false
      }
    },

    mapApiDataToActivities (data) {
      const activities = []

      if (data.data && Array.isArray(data.data)) {
        return data.data.map(item => this.transformActivityItem(item))
      }

      if (Array.isArray(data)) {
        return data.map(item => this.transformActivityItem(item))
      }

      return activities
    },

    transformActivityItem (item) {
      const statusMap = {
        2: { text: this.$t('activities.status_warning'), className: 'activity-warning' },
        1: { text: this.$t('activities.status_not_working'), className: 'activity-inactive' },
        3: { text: this.$t('activities.status_active'), className: 'activity-active' },
        0: { text: this.$t('activities.status_registration'), className: 'activity-default' }
      }

      const statusInfo = statusMap[item.status] || {
        text: 'Unknown',
        className: 'activity-default'
      }

      return {
        status: statusInfo.text,
        days: item.days,
        loadCount: `${item.total_done || 0}/${item.total_planned || 0}`,
        className: statusInfo.className
      }
    },

    closeModal () {
      this.$emit('close')
    },

    handleEscapeKey (event) {
      if (event.key === 'Escape' && this.show) {
        this.closeModal()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.monthly-activities-modal {
  .modal-header {
    padding: 2rem 2.4rem 1.6rem !important;
    border-bottom: 0.1rem solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .modal-title {
      font-size: 1.6rem;
      font-weight: 600;
      color: #333;
      margin: 0 0 0.5rem 0;
    }
  }
  
  .modal-content {
    padding: 2rem 2.4rem 2.4rem !important;
  }
}

.client-name {
  font-size: 1.4rem;
  font-weight: 500;
  color: #666;
  margin: 0;
  word-break: break-all;
  margin-top: 1rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;

  .loading-text {
    margin-top: 1rem;
    color: #666;
    font-size: 1.4rem;
  }
}

.error-container {
  text-align: center;
  padding: 2rem 0;

  .error-text {
    color: #d32f2f;
    margin-bottom: 1rem;
    font-size: 1.4rem;
  }
}

.empty-state {
  text-align: center;
  padding: 2rem 0;
  color: #666;
  font-size: 1.4rem;
}

.activities-table {
  width: 100%;
}

.table-header-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1.2rem;
  margin-bottom: 1.2rem;

  .header-cell {
    font-size: 1.2rem;
    color: #666;
    font-weight: 500;
    text-align: center;

    &.status-header {
      text-align: left;
    }
  }
}

.modal-title {
  margin-top: -0.7rem;
}

.v-card__content {
  margin-top: 1rem;
  border-top: 0.1rem solid #D7D7D7;
  padding: 1rem;
}

.activity-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 1.2rem;
  padding: 1.2rem 1.6rem;
  margin-bottom: 0.8rem;
  border-radius: 0.8rem;
  align-items: center;
  transition: all 0.2s;

  &:last-child {
    margin-bottom: 0;
  }

  .status-label {
    font-weight: 500;
    font-size: 1.4rem;
  }

  .status-days {
    text-align: center;
    font-weight: 600;
    font-size: 1.4rem;
  }

  .status-loads {
    text-align: center;
    font-weight: 500;
    font-size: 1.4rem;
  }
}

.activity-active {
  background: #91EA91;
  color: #000;
}

.activity-warning {
  background: #FBE268;
  color: #000;
}

.activity-inactive {
  background: #FF9191;
  color: #000;
}

.activity-default {
  background: #f5f5f5;
  color: #000;
}

@media (max-width: 48rem) {
  .monthly-activities-modal {
    .modal-header {
      padding: 1.6rem 2rem 1.2rem !important;

      .modal-title {
        font-size: 1.4rem;
      }
    }

    .modal-content {
      padding: 1.6rem 2rem 2rem !important;
    }
  }

  .table-header-row {
    gap: 0.8rem;

    .header-cell {
      font-size: 1.1rem;
    }
  }

  .activity-row {
    gap: 0.8rem;
    padding: 1rem 1.2rem;

    .status-label,
    .status-days,
    .status-loads {
      font-size: 1.3rem;
    }
  }

  .loading-text,
  .error-text,
  .empty-state {
    font-size: 1.3rem;
  }
}
</style>